var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from "axios";
import { MAX_FILE_SIZE_MB, stageToEndpoint, } from "../types";
/**
 * Get File Management Service api url for environment
 *
 * @param stage
 * @returns endpoint
 */
export const getFileServiceApiUrl = (stage) => {
    const endpoint = stageToEndpoint[stage];
    if (endpoint === undefined) {
        throw new Error("App stage not recognized");
    }
    return endpoint;
};
/**
 * Constrain consumer's max file size to the max allowed by service
 *
 * @param maxFileSizeMbFromConsumer
 * @returns maxFileSizeMb
 */
export const applyFileSizeCeiling = (maxFileSizeMbFromConsumer) => Math.min(maxFileSizeMbFromConsumer, MAX_FILE_SIZE_MB);
/**
 * Append description
 *
 * @param description
 * @param text
 * @returns updated description
 */
export const appendDescription = (description, text) => {
    if (description === undefined) {
        return text;
    }
    return `${description} ${text}`;
};
/**
 * Format description for display
 *
 * @param argsObj
 * @returns description
 */
export const formatDescription = ({ showContentTypes, contentTypes, showMaxFileSize, maxFileSizeInMegaBytes, }) => {
    let description;
    if (showContentTypes) {
        const supportedFormats = contentTypes.map((format) => format.replace("image/", "."));
        description = appendDescription(description, `Supported formats ${supportedFormats.join(" ")}.`);
    }
    if (showMaxFileSize) {
        const maxFileSize = applyFileSizeCeiling(maxFileSizeInMegaBytes);
        description = appendDescription(description, `Max file size ${maxFileSize} MB.`);
    }
    return description;
};
/**
 * Validate file
 *
  @param argsObj
 * @returns {ValidateFileResult} validateFileResult
 */
export const validateFile = ({ file, contentTypes, maxFileSizeInMegaBytes, }) => {
    const maxFileSizeMb = applyFileSizeCeiling(maxFileSizeInMegaBytes);
    const validationErrors = [];
    // 1024 * 1024 bytes is 1 MB
    if (file.size / (1024 * 1024) > maxFileSizeMb) {
        validationErrors.push(`File is too large (max ${maxFileSizeMb} MB)`);
    }
    else if (!contentTypes.includes(file.type)) {
        validationErrors.push(`File type ${file.type} is not allowed`);
    }
    return {
        file,
        isValid: validationErrors.length === 0,
        validationErrors,
    };
};
/**
 * Create presigned url and upload file to s3
 *
 * @param argsObj
 * @returns {Promise<UploadFileResult>} result
 */
export const uploadFile = ({ stage, authToken, file, objectId, objectType, }) => __awaiter(void 0, void 0, void 0, function* () {
    const result = {
        file,
        isSuccess: false,
    };
    let presignedUrl;
    try {
        const presignedUrlConfig = {
            method: "post",
            url: `${getFileServiceApiUrl(stage)}/presignedUrl`,
            data: {
                name: file.name,
                contentLength: file.size,
                contentType: file.type,
                objectId,
                objectType,
            },
            headers: {
                Authorization: authToken,
            },
            validateStatus: (status) => status === 200,
        };
        const res = yield axios(presignedUrlConfig);
        presignedUrl = res.data.presignedUrl;
        result.fileId = res.data.fileId;
    }
    catch (err) {
        result.error = err;
        return result;
    }
    try {
        const s3PutObjectConfig = {
            method: "put",
            url: presignedUrl,
            data: file,
            headers: {
                "Content-Type": file.type,
            },
            validateStatus: (status) => status === 200,
        };
        yield axios(s3PutObjectConfig);
        result.isSuccess = true;
    }
    catch (err) {
        result.error = err;
        return result;
    }
    return result;
});
